import {
    getQueryFromURLSearchParams,
    getURLSearchParamsFromQuery,
} from '@schibsted-nmp/search-frontend-clients';
import React from 'react';
import type { SearchParams } from '../state/AppStateTypes';
import {
    readBucketCookieServer,
    writeBucketCookie,
} from '../state/bucketCookie';
import { useSearchParams } from './SearchParamsStore';

const LOCATION_KEY = 'locationSearchParams';
const LOCATION_PARAMS_NAME = 'location';

const EMPTY_SEARCH_PARAMS: SearchParams = {};

export function useLocationParamsCookieState() {
    const { searchParams } = useSearchParams();

    React.useEffect(() => {
        if (LOCATION_PARAMS_NAME in searchParams) {
            const locationParams = {
                [LOCATION_PARAMS_NAME]: searchParams[LOCATION_PARAMS_NAME],
            };

            writeBucketCookie(LOCATION_KEY, locationParams);
        } else {
            writeBucketCookie(LOCATION_KEY, EMPTY_SEARCH_PARAMS);
        }
    }, [searchParams]);
}

export function readPersistedLocationParams(
    cookies: Record<string, string>,
    searchParams: SearchParams,
): SearchParams {
    try {
        const locationParams = getPersistedLocationParams(cookies);

        const urlParams = getURLSearchParamsFromQuery(searchParams);
        const locationUrlParams = getURLSearchParamsFromQuery(locationParams);

        const locationParamKeys = Array.from(locationUrlParams.keys());
        const hasOriginalLocationParams = locationParamKeys.some((key) =>
            urlParams.has(key),
        );

        // Merge location parameters with URL parameters
        if (!hasOriginalLocationParams) {
            for (const [key, value] of locationUrlParams.entries()) {
                urlParams.append(key, value);
            }
        }

        return getQueryFromURLSearchParams(urlParams);
    } catch {
        return searchParams;
    }
}

function getPersistedLocationParams(
    cookies: Record<string, string>,
): SearchParams {
    const locationValue = readBucketCookieServer(cookies, LOCATION_KEY);
    return typeof locationValue === 'object' && locationValue !== null
        ? (locationValue as SearchParams)
        : EMPTY_SEARCH_PARAMS;
}
